import makers from "../data/makers.json" with { type: "json" };

/**
 * Part makers as parts shops name them. Case, spaces, punctuation and accents never count
 * ("LEMFÖRDER", "Lemförder", "lemforder" are one maker); `data/makers.json` lists the other words
 * a maker goes by ("FEBI" for "febi bilstein").
 */

/** A maker's name with case, accents and everything but letters and digits taken out. */
function fold(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, "");
}

function readMakers(data: unknown): Map<string, string> {
  const problems: string[] = [];
  const byFolded = new Map<string, string>();
  const list = (data as { makers?: unknown }).makers;
  if (!Array.isArray(list)) throw new Error("makers.json: expected a list of makers");
  list.forEach((maker: unknown, i: number) => {
    const { name, spellings } = (maker ?? {}) as { name?: unknown; spellings?: unknown };
    if (typeof name !== "string" || !name.trim() || !Array.isArray(spellings)) {
      problems.push(`makers.json.makers[${i}]: expected a name and a list of spellings`);
      return;
    }
    for (const spelling of [name, ...spellings]) {
      const key = typeof spelling === "string" ? fold(spelling) : "";
      if (!key) problems.push(`makers.json.makers[${i}]: empty spelling`);
      else if (byFolded.has(key) && byFolded.get(key) !== name) {
        problems.push(`makers.json.makers[${i}]: "${spelling}" is already ${byFolded.get(key)}`);
      } else byFolded.set(key, name);
    }
  });
  if (problems.length > 0) throw new Error(`Maker data is invalid:\n- ${problems.join("\n- ")}`);
  return byFolded;
}

const MAKERS = readMakers(makers);

/**
 * The name a maker is shown by: the listed name for a known spelling ("FEBI" → "febi bilstein"),
 * else the text as given with its spaces tidied.
 */
export function normalizeMakerName(name: string): string {
  return MAKERS.get(fold(name)) ?? name.trim().replace(/\s+/g, " ");
}

/** What two spellings of one maker share, for keys and comparisons: "febibilstein". */
export function makerKey(name: string): string {
  return fold(normalizeMakerName(name));
}
